import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconButton, Tooltip, useOutsideClick } from '@chakra-ui/react';

/** Accessible help for hover, keyboard focus, and touch; independent of disabled controls. */
export default function AdminHelpTooltip({ label, description, icon, className }) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const closeTimer = useRef(null);
  const show = useCallback(() => {
    clearTimeout(closeTimer.current);
    setIsOpen(true);
  }, []);
  const dismiss = useCallback(() => {
    clearTimeout(closeTimer.current);
    setIsOpen(false);
  }, []);
  // Allow the pointer to cross the gap from the trigger to the tooltip.
  const hide = useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setIsOpen(false), 180);
  }, []);
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  useOutsideClick({ ref: triggerRef, handler: dismiss });
  useEffect(() => {
    if (!isOpen) return undefined;
    const onEscape = event => { if (event.key === 'Escape') dismiss(); };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [isOpen, dismiss]);

  return <Tooltip label={description} isOpen={isOpen} onOpen={show} onClose={hide}
    hasArrow placement="top" maxW="min(320px, calc(100vw - 32px))"
    bg="gray.800" color="white" px={3} py={2} borderRadius="md"
    fontSize="sm" fontWeight="normal" lineHeight="1.5" pointerEvents="auto"
    closeOnClick={false} closeOnMouseDown={false} closeOnEsc={false}
    onMouseEnter={show} onMouseLeave={hide}>
    <IconButton ref={triggerRef} type="button" className={className}
      aria-label={label} icon={icon}
      variant="ghost" colorScheme="blue" color="gray.700" size="sm" w={10} h={10} minW={10} p={0}
      onClick={show} onKeyDown={event => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          dismiss();
        }
      }} />
  </Tooltip>;
}
